import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, HUB_META } from "../lib/api";
import AppShell from "../components/AppShell";
import ShareBar from "../components/ShareBar";
import AuthorAvatar from "../components/AuthorAvatar";
import { useAuth } from "../lib/auth";
import { Globe, Buildings, LinkedinLogo, FacebookLogo, InstagramLogo, YoutubeLogo, XLogo, Robot, ArrowRight, Users, PencilSimpleLine } from "@phosphor-icons/react";

const SOCIAL_ICONS = { linkedin: LinkedinLogo, x: XLogo, facebook: FacebookLogo, instagram: InstagramLogo, youtube: YoutubeLogo };

export default function OrgDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [org, setOrg] = useState(null);
  const [team, setTeam] = useState([]);
  const [agents, setAgents] = useState([]);

  useEffect(() => {
    (async () => {
      const [o, t, a] = await Promise.all([
        api.get(`/orgs/${slug}`),
        api.get(`/orgs/${slug}/team`),
        api.get(`/agents`),
      ]);
      setOrg(o.data);
      setTeam(t.data || []);
      setAgents(a.data || []);
    })().catch(() => {});
  }, [slug]);

  if (!org) return <AppShell><div className="py-20 text-center label-mono"><span className="spinner mr-2 align-middle" />LOADING ORG...</div></AppShell>;

  const isOwner = user && (user.id === org.owner_id);
  const hubColor = HUB_META[org.hub_key]?.color || "#E60012";
  const sponsored = agents.filter((a) => (org.sponsored_agent_keys || []).includes(a.key));
  const bannerSrc = org.banner_url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${org.banner_url}` : org.banner_url;
  const logoSrc = org.logo_url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${org.logo_url}` : org.logo_url;

  return (
    <AppShell>
      <div data-testid="org-detail-page">
        {/* Banner */}
        <div className="relative h-48 md:h-64 border border-[#27272A] mb-0 overflow-hidden bg-[#0A0A0C]">
          {bannerSrc ? (
            <img src={bannerSrc} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full blueprint-grid-dense" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-transparent to-transparent" />
        </div>

        {/* Header card */}
        <div className="border border-[#27272A] border-t-0 -mt-px p-6 mb-8 grid md:grid-cols-[140px_1fr_auto] gap-6 items-start">
          <div className="-mt-16">
            <div className="w-32 h-32 rounded-full border-4 border-[#0A0A0C] bg-[#1A1A1D] overflow-hidden">
              {logoSrc ? (
                <img src={logoSrc} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center font-display text-5xl" style={{ background: "#E60012", color: "#fff", fontWeight: 900 }}>{org.name[0]}</div>
              )}
            </div>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="hub-dot" style={{ color: hubColor }} />
              <span className="label-mono" style={{ color: hubColor }}>{org.hub_key ? org.hub_key.toUpperCase() : "ALL HUBS"} · ORGANIZATION</span>
            </div>
            <h1 className="font-display text-white mb-2" style={{ fontSize: 40, fontWeight: 900, letterSpacing: "-0.035em" }} data-testid="org-name">{org.name}</h1>
            {org.tagline && <p className="text-zinc-300 text-lg max-w-2xl">{org.tagline}</p>}

            <div className="flex items-center gap-3 mt-4 flex-wrap">
              {org.website && (
                <a href={org.website} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" data-testid="org-website">
                  <Globe size={12} /> Website
                </a>
              )}
              {Object.entries(org.social || {}).filter(([_, v]) => v).map(([k, v]) => {
                const Icon = SOCIAL_ICONS[k];
                if (!Icon) return null;
                return (
                  <a key={k} href={v} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" data-testid={`org-social-${k}`}>
                    <Icon size={13} weight="fill" />
                  </a>
                );
              })}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {isOwner && <Link to={`/org/${slug}`} className="btn btn-ghost btn-sm" data-testid="org-edit-btn"><PencilSimpleLine size={12} /> Manage</Link>}
            <ShareBar url={`${window.location.origin}/org/${org.slug}`} title={`${org.name} on iAAM`} summary={org.tagline || ""} />
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-8">
            {org.about && (
              <section>
                <div className="label-mono mb-3" style={{ color: "#E60012" }}>ABOUT</div>
                <p className="text-zinc-200 leading-relaxed whitespace-pre-wrap" data-testid="org-about">{org.about}</p>
              </section>
            )}

            {sponsored.length > 0 && (
              <section>
                <div className="label-mono mb-3 flex items-center gap-1.5" style={{ color: "#00E5FF" }}>
                  <Robot size={11} weight="fill" /> SPONSORED AGENTS
                </div>
                <div className="grid md:grid-cols-2 gap-3" data-testid="org-sponsored">
                  {sponsored.map((a) => (
                    <Link key={a.key} to={`/agent/${a.key}`} className="card card-hover p-4 flex items-center gap-3" style={{ borderTop: `2px solid ${a.color}` }}>
                      <AuthorAvatar name={a.name} avatarUrl={a.avatar_url} size={48} rounded={false} bg={a.color} />
                      <div className="min-w-0 flex-1">
                        <div className="label-mono mb-0.5" style={{ color: a.color, fontSize: 9 }}>{a.role.replace(" Agent", "").toUpperCase()}</div>
                        <div className="text-sm text-white">{a.name}</div>
                        <div className="text-xs text-zinc-400 line-clamp-1">{a.specialty}</div>
                      </div>
                      <ArrowRight size={12} weight="bold" className="text-zinc-500" />
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>

          <aside className="lg:col-span-4 space-y-6">
            <div className="card p-5">
              <div className="label-mono mb-3 flex items-center gap-1.5"><Users size={11} /> TEAM · {team.length}</div>
              {team.length === 0 ? (
                <p className="text-xs text-zinc-500">No team members yet.</p>
              ) : (
                <ul className="space-y-3" data-testid="org-team">
                  {team.map((u) => (
                    <li key={u.id} className="flex items-center gap-3">
                      <AuthorAvatar name={u.name} avatarUrl={u.avatar_url} size={28} />
                      <Link to={`/profile/${u.id}`} className="text-sm text-white hover:underline">{u.name}</Link>
                      {u.id === org.owner_id && (
                        <span className="label-mono ml-auto" style={{ color: "#E60012", fontSize: 8 }}>OWNER</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>
        </div>
      </div>
    </AppShell>
  );
}
